/**
 * Stable reason codes for public-sentiment rows that carry no local stance.
 *
 * `scopeSentimentEntry` stores its reason as fixed English text on each SED
 * row. Report-time projections (ledger, "unscored by reason" chart, prompt
 * tables) need a closed code instead. Classifying at report time — by exact
 * match against the frozen strings — keeps the collector, the scope version,
 * query fingerprints and stored receipts unchanged.
 *
 * Anything that is not one of the fixed strings (a source adapter's own
 * `assessmentReason` passed through when only the confidence was unusable, or
 * wording from an older scope version) maps to `no-usable-assessment`, with
 * the original text kept in `reasonNote`. It is never guessed into a more
 * specific code such as `ambiguous`.
 */
import { SENTIMENT_UNSCORED_REASONS, type SentimentUnscoredReasonCode } from './publicSentimentScope';

/**
 * `context-not-local` is not a scoping outcome: it marks a statewide or
 * national row that WAS assessed at its own scope, shown in a report with no
 * dated local stance. There it reads "Not assessed" instead of a stance word.
 */
export type SentimentReasonCode = SentimentUnscoredReasonCode | 'context-not-local';

export const SENTIMENT_REASON_CODES: readonly SentimentReasonCode[] = Object.freeze([
  ...(Object.keys(SENTIMENT_UNSCORED_REASONS) as SentimentUnscoredReasonCode[]),
  'context-not-local',
]);

/** Short reader labels (SPEC §1 chips). Never a stance word. */
export const SENTIMENT_REASON_LABELS: Readonly<Record<SentimentReasonCode, string>> = Object.freeze({
  'agenda-only': 'Agenda only — planned business, no testimony',
  undated: 'Source date unknown',
  'first-seen': 'Only a first-seen date',
  truncated: 'Passage truncated',
  'out-of-window': 'Outside the observation window',
  'policy-only': 'Agency or policy action, not opinion',
  ambiguous: 'Mixed or unclear stance',
  'outside-speaker': 'Speaker from another locality',
  'publisher-only': 'Local publisher only, no local link',
  'not-selected-technology': 'Not a selected technology',
  'no-usable-assessment': 'No usable stance assessment',
  'context-not-local': 'Context record — not assessed as local stance',
});

const BY_TEXT = new Map<string, SentimentUnscoredReasonCode>(
  (Object.entries(SENTIMENT_UNSCORED_REASONS) as Array<[SentimentUnscoredReasonCode, string]>).map(([code, text]) => [text, code]),
);

/** Longest free-text reason kept on a projected row (code points). */
export const SENTIMENT_REASON_NOTE_MAX = 300;

/** Truncate by code point (never split a surrogate pair); marks truncation. */
export function truncateCodePoints(text: string, max: number): { text: string; truncated: boolean } {
  const points = Array.from(text);
  return points.length <= max
    ? { text, truncated: false }
    : { text: `${points.slice(0, Math.max(0, max - 1)).join('')}…`, truncated: true };
}

/**
 * Classify a stored `assessmentReason`. Exact match only; unmatched,
 * pass-through and legacy text become `no-usable-assessment` + `reasonNote`.
 */
export function sentimentReasonCode(reason: string | null | undefined): { reasonCode: SentimentReasonCode; reasonNote?: string } {
  const text = typeof reason === 'string' ? reason : '';
  const code = BY_TEXT.get(text);
  if (code) return { reasonCode: code };
  const note = text.trim();
  return note
    ? { reasonCode: 'no-usable-assessment', reasonNote: truncateCodePoints(note, SENTIMENT_REASON_NOTE_MAX).text }
    : { reasonCode: 'no-usable-assessment' };
}
